/**
 * Search Modal Component
 * Uses Shopify Predictive Search API (/search/suggest.json)
 * Compatible with Shopify Search & Discovery app
 */
class SearchModal extends HTMLElement {
  constructor() {
    super();

    let _this = this;
    this.openSearchModal = document.querySelectorAll('.open-searchform');
    this.searchFormContainer = this.querySelector('.searchform');
    this.searchresults = this.querySelector('.predictive-search');
    this.searchQuery = this.querySelector('form[action="/search"] [name="q"]');
    this.loadingIndicator = this.querySelector('.predictive-search__loading');
    this.emptyState = this.querySelector('.predictive-search__empty');

    // Sections for different resource types
    this.productSection = this.querySelector('.predictive-search__products');
    this.collectionSection = this.querySelector('.predictive-search__collections');
    this.pageSection = this.querySelector('.predictive-search__pages');
    this.articleSection = this.querySelector('.predictive-search__articles');

    this.abortController = null;
    this.typingTimer = null;
    this.cachedResults = {};

    this.searchFormContainer.addEventListener('keyup', (event) => {
      event.code.toUpperCase() === 'ESCAPE' && this.close();
    });

    this.openSearchModal.forEach(toggleBtn => {
      toggleBtn.addEventListener('click', function (event) {
        _this.onToggleBtnClick(event);
      });
      toggleBtn.setAttribute('role', 'button');
      toggleBtn.setAttribute('aria-expanded', 'false');
    });

    this.querySelector('.search-modal__close-button').addEventListener('click', this.close.bind(this));

    this.searchQuery.addEventListener('input', this.queryInput.bind(this));
    this.searchQuery.addEventListener('keydown', () => {
      clearTimeout(this.typingTimer);
    });
  }

  /**
   * To check if search modal is open or closed
   * @returns {boolean}
   */
  isOpen() {
    return this.searchFormContainer.hasAttribute('open');
  }

  /**
   * Toggle Search Modal on search Icon click
   * @param {Event} event
   */
  onToggleBtnClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.searchFormContainer.hasAttribute('open')
      ? this.close()
      : this.open(event);
  }

  /**
   * Close Search Modal on body click if not within the Modal
   * @param {Event} event
   */
  onBodyClick(event) {
    if (!this.contains(event.target)) this.close(false);
  }

  /**
   * Debounced input handler — 300ms delay
   * @param {Event} event
   */
  queryInput(event) {
    let _this = this;
    let queryValue = event.currentTarget.value.trim();
    clearTimeout(_this.typingTimer);
    _this.typingTimer = setTimeout(function () {
      _this._queryChange(queryValue);
    }, 300);
  }

  /**
   * Fetch predictive search results from Shopify API
   * @param {string} queryValue
   */
  _queryChange(queryValue) {
    if (!queryValue || queryValue.length <= 0) {
      this._hideResults();
      return;
    }

    // Check cache first
    const cacheKey = queryValue.toLowerCase();
    if (this.cachedResults[cacheKey]) {
      this._renderResults(this.cachedResults[cacheKey], queryValue);
      return;
    }

    // Abort any in-flight request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    // Show loading state
    this._showLoading();

    const searchTypeInput = this.searchFormContainer.querySelector('[name="type"]');
    const searchType = searchTypeInput ? searchTypeInput.value : 'product';
    const searchUrl = window.routes.predictive_search || window.routes.predictive_searh;

    fetch(
      `${searchUrl}?q=${encodeURIComponent(queryValue)}&resources[type]=${searchType}&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor`,
      { signal: this.abortController.signal }
    )
      .then(response => {
        if (!response.ok) throw new Error('Search request failed');
        return response.json();
      })
      .then(response => {
        const searchResults = response.resources.results;
        // Cache this result
        this.cachedResults[cacheKey] = searchResults;
        this._renderResults(searchResults, queryValue);
      })
      .catch((e) => {
        if (e.name === 'AbortError') return; // Ignore aborted requests
        console.error('Predictive search error:', e);
        this._hideLoading();
      })
      .finally(() => {
        this.abortController = null;
      });
  }

  /**
   * Render all search result sections
   * @param {Object} searchResults
   * @param {string} queryValue
   */
  _renderResults(searchResults, queryValue) {
    this._hideLoading();

    const searchTypeInput = this.searchFormContainer.querySelector('[name="type"]');
    const searchType = searchTypeInput ? searchTypeInput.value : 'product';

    const hasProducts = searchResults.products && searchResults.products.length > 0;
    const hasCollections = searchResults.collections && searchResults.collections.length > 0;
    const hasPages = searchResults.pages && searchResults.pages.length > 0;
    const hasArticles = searchResults.articles && searchResults.articles.length > 0;
    const hasAnyResults = hasProducts || hasCollections || hasPages || hasArticles;

    if (!hasAnyResults) {
      this._showEmptyState(queryValue);
      return;
    }

    // Hide empty state
    if (this.emptyState) this.emptyState.style.display = 'none';

    // Render products
    if (this.productSection) {
      if (hasProducts) {
        this.productSection.querySelector('.predictive-search__list').innerHTML =
          this._renderProductItems(searchResults.products);
        this.productSection.style.display = 'block';
      } else {
        this.productSection.style.display = 'none';
      }
    }

    // Render collections
    if (this.collectionSection) {
      if (hasCollections) {
        this.collectionSection.querySelector('.predictive-search__list').innerHTML =
          this._renderCollectionItems(searchResults.collections);
        this.collectionSection.style.display = 'block';
      } else {
        this.collectionSection.style.display = 'none';
      }
    }

    // Render pages
    if (this.pageSection) {
      if (hasPages) {
        this.pageSection.querySelector('.predictive-search__list').innerHTML =
          this._renderPageItems(searchResults.pages);
        this.pageSection.style.display = 'block';
      } else {
        this.pageSection.style.display = 'none';
      }
    }

    // Render articles
    if (this.articleSection) {
      if (hasArticles) {
        this.articleSection.querySelector('.predictive-search__list').innerHTML =
          this._renderArticleItems(searchResults.articles);
        this.articleSection.style.display = 'block';
      } else {
        this.articleSection.style.display = 'none';
      }
    }

    // Update "View all" link
    if (this.searchresults) {
      const viewAllLink = this.searchresults.querySelector('.predictive-search-footer a');
      if (viewAllLink) {
        viewAllLink.href = `/search?q=${encodeURIComponent(queryValue)}&type=${searchType}&options[prefix]=last`;
      }
      this.searchresults.style.display = 'block';
    }
  }

  /**
   * Render product result items
   * @param {Array} products
   * @returns {string} HTML
   */
  _renderProductItems(products) {
    return products.map(product => {
      const priceAmount = product.price ? product.price * 100 : 0;
      const productPrice = Shopify.formatMoney(priceAmount, window.globalVariables.money_format);

      let comparePrice = '';
      if (product.compare_at_price && product.compare_at_price > product.price) {
        const compareAmount = product.compare_at_price * 100;
        const comparePriceFormatted = Shopify.formatMoney(compareAmount, window.globalVariables.money_format);
        comparePrice = `<s class="predictive-search__compare-price text-gray-500">${comparePriceFormatted}</s>`;
      }

      const imgSrc = product.image
        ? this._getSizedImageUrl(product.image, '100x')
        : (window.globalVariables.noImageUrl || '');
      const imgTag = imgSrc
        ? `<img src="${imgSrc}" alt="${this._escapeHtml(product.title)}" loading="lazy" width="80" height="80">`
        : `<div class="predictive-search__no-image"><span class="icon-search"></span></div>`;

      return `<div class="col-sm-6 px-2"><div class="predictive-search-product my-2 mx-n2 row">
                <div class="col-3 px-2">
                    <a href="${product.url}">${imgTag}</a>
                </div>
                <div class="col-9 px-2">
                    <h6 class="product-title"><a href="${product.url}">${this._escapeHtml(product.title)}</a></h6>
                    <p class="product-price mb-0">${comparePrice} ${productPrice}</p>
                </div>
            </div></div>`;
    }).join('');
  }

  /**
   * Render collection result items
   * @param {Array} collections
   * @returns {string} HTML
   */
  _renderCollectionItems(collections) {
    return collections.map(collection => {
      return `<div class="predictive-search__item">
                <a href="${collection.url}" class="d-flex align-items-center py-2">
                    <span class="icon-search me-2"></span>
                    <span>${this._escapeHtml(collection.title)}</span>
                </a>
            </div>`;
    }).join('');
  }

  /**
   * Render page result items
   * @param {Array} pages
   * @returns {string} HTML
   */
  _renderPageItems(pages) {
    return pages.map(page => {
      return `<div class="predictive-search__item">
                <a href="${page.url}" class="d-flex align-items-center py-2">
                    <span class="icon-search me-2"></span>
                    <span>${this._escapeHtml(page.title)}</span>
                </a>
            </div>`;
    }).join('');
  }

  /**
   * Render article result items
   * @param {Array} articles
   * @returns {string} HTML
   */
  _renderArticleItems(articles) {
    return articles.map(article => {
      const imgSrc = article.image ? this._getSizedImageUrl(article.image, '80x') : '';
      const imgTag = imgSrc
        ? `<img src="${imgSrc}" alt="${this._escapeHtml(article.title)}" loading="lazy" width="60" height="60" class="me-3 rounded">`
        : '';

      return `<div class="predictive-search__item">
                <a href="${article.url}" class="d-flex align-items-center py-2">
                    ${imgTag}
                    <div>
                        <h6 class="mb-0">${this._escapeHtml(article.title)}</h6>
                        ${article.published_at ? `<small class="text-gray-500">${new Date(article.published_at).toLocaleDateString()}</small>` : ''}
                    </div>
                </a>
            </div>`;
    }).join('');
  }

  /**
   * Show loading spinner
   */
  _showLoading() {
    if (this.loadingIndicator) this.loadingIndicator.style.display = 'flex';
    if (this.emptyState) this.emptyState.style.display = 'none';
    // Show results container so loading is visible
    if (this.searchresults) this.searchresults.style.display = 'block';
    // Hide previous result sections
    this._clearSections();
  }

  /**
   * Hide loading spinner
   */
  _hideLoading() {
    if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';
  }

  /**
   * Show empty state
   * @param {string} queryValue
   */
  _showEmptyState(queryValue) {
    this._clearSections();
    if (this.emptyState) {
      const msgEl = this.emptyState.querySelector('.predictive-search__empty-message');
      if (msgEl) msgEl.textContent = `No results found for "${queryValue}"`;
      this.emptyState.style.display = 'block';
    }
    if (this.searchresults) {
      this.searchresults.style.display = 'block';
      const viewAllLink = this.searchresults.querySelector('.predictive-search-footer a');
      if (viewAllLink) viewAllLink.style.display = 'none';
    }
  }

  /**
   * Hide all results
   */
  _hideResults() {
    this._clearSections();
    if (this.searchresults) this.searchresults.style.display = 'none';
    if (this.emptyState) this.emptyState.style.display = 'none';
    if (this.loadingIndicator) this.loadingIndicator.style.display = 'none';
  }

  /**
   * Clear all section content
   */
  _clearSections() {
    [this.productSection, this.collectionSection, this.pageSection, this.articleSection].forEach(section => {
      if (section) {
        const list = section.querySelector('.predictive-search__list');
        if (list) list.innerHTML = '';
        section.style.display = 'none';
      }
    });
  }

  /**
   * Get a sized image URL from Shopify CDN
   * @param {string} url
   * @param {string} size e.g. '100x'
   * @returns {string}
   */
  _getSizedImageUrl(url, size) {
    if (!url) return '';
    // Handle Shopify CDN URLs
    const match = url.match(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif|webp)(\?v=\d+)?$/i);
    if (match) {
      const prefix = url.split(match[0]);
      return `${prefix[0]}_${size}${match[0]}`;
    }
    return url;
  }

  /**
   * Escape HTML to prevent XSS
   * @param {string} text
   * @returns {string}
   */
  _escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Open Search Modal
   * @param {Event} event
   */
  open(event) {
    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);
    this.searchFormContainer.setAttribute('open', true);
    this.classList.add('open__modal');
    this.removeAttribute('style');
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.querySelector('#Search-In-Modal').click();
    Utility.trapFocus(this);
    Utility.forceFocus(document.getElementById('Search-In-Modal'));
  }

  /**
   * Close Search Modal
   * @param {boolean} focusToggle
   */
  close(focusToggle = true) {
    Utility.removeTrapFocus();
    this.searchFormContainer.removeAttribute('open');
    this.classList.remove('open__modal');
    document.body.removeEventListener('click', this.onBodyClickEvent);

    // Reset search input
    if (this.searchQuery) this.searchQuery.value = '';

    // Abort any pending request
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }

    this._hideResults();
  }
}
customElements.define('search-modal', SearchModal);